import React from "react";
import {
  BrandTheme,
  StyleConfig,
  ThemeConfig,
} from "../types/GamifiedQuiz.type";
import { extractGradientColors, hexToGlassColor } from "../../../../general";

export class ThemeManager {
  private brandTheme: BrandTheme;
  private styleConfig: StyleConfig;
  private themeConfig: ThemeConfig;
  private questThemeData: any;

  constructor(
    brandTheme: BrandTheme = {},
    styleConfig: StyleConfig = {},
    themeConfig: ThemeConfig = {},
    questThemeData: any = {}
  ) {
    this.brandTheme = brandTheme;
    this.styleConfig = styleConfig;
    this.themeConfig = themeConfig;
    this.questThemeData = questThemeData;
  }
  getTextInputErrorText(): string {
    return this.styleConfig?.TextError?.text || "Invalid text format";
  }
  getTextInputErrorStyles(): string {
    return this.styleConfig?.TextError?.errorStyle;
  }
  getTextInputStyles(): React.CSSProperties {
    return {
      borderColor: this.styleConfig?.Input?.borderColor,
      color:
        this.styleConfig?.Input?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Input,
    };
  }
  getEmailIconColor(): string {
    return (
      this.styleConfig?.Input?.color ||
      this?.brandTheme?.titleColor ||
      this?.themeConfig?.primaryColor ||
      "#B9B9B9"
    );
  }
  getNumberInputErrorText(): string {
    return this.styleConfig?.NumberError?.text || "Invalid text format";
  }

  getNumberInputErrorStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.NumberError?.errorStyle,
    };
  }
  getThanksPopupStyles(): React.CSSProperties {
    return {
      background:
        this.styleConfig?.ThanksPopup?.background ||
        this.themeConfig?.backgroundColor,
      fontFamily: this.themeConfig?.fontFamily,
    };
  }

  getCancelButtonStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.IconColor?.color ||
        this.themeConfig?.IconColor?.color ||
        this.questThemeData?.IconColor?.color ||
        this.brandTheme?.IconColor?.color,
    };
  }

  getThanksPopUpFooterStyles(): React.CSSProperties {
    return {
      background: this.styleConfig?.ThanksPopUpFooter?.background,
      color:
        this.styleConfig?.ThanksPopUpFooter?.color ||
        this.themeConfig?.secondaryColor,
      fontFamily: this.themeConfig?.fontFamily,
    };
  }

  getThanksPopupHeadingStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.ThanksPopupHeading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor ||
        "var(--Neutral-Black-400, #2C2C2C)",
      fontFamily: this.themeConfig?.fontFamily,
    };
  }

  getThanksPopupDescriptionStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.ThanksPopupDescription?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.secondaryColor ||
        "var(--Neutral-Black-100, #939393)",
      fontFamily: this.themeConfig?.fontFamily,
    };
  }

  getThanksPopUpGotoHomeStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.ThanksPopUpGotoHome?.borderColor ||
        this.themeConfig?.borderColor,
      background: this.styleConfig?.ThanksPopUpGotoHome?.background,
      color:
        this.styleConfig?.ThanksPopUpGotoHome?.color ||
        this.themeConfig?.secondaryColor,
      fontFamily: this.themeConfig?.fontFamily || "Figtree",
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px",
    };
  }

  getHeaderFormStyles(): React.CSSProperties {
    return {
      borderRadius:
        this.styleConfig?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius ||
        this.themeConfig?.borderRadius ,
    };
  }

  getFormStyles(): React.CSSProperties {
    return {
      background:
        this.styleConfig?.Form?.backgroundColor ||
        this.brandTheme?.background ||
        this.themeConfig?.backgroundColor,
      borderRadius:
        this.styleConfig?.Form?.borderRadius ||
        this.questThemeData?.borderRadius ||
        this.brandTheme?.borderRadius || '12px',
      fontFamily:
        this.brandTheme?.fontFamily ||
        this.themeConfig.fontFamily ||
        "'Figtree', sans-serif",
      ...this.styleConfig?.Form,
    };
  }

  getQuestionContainerStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.Question?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Question,
      fontFamily: this.themeConfig?.fontFamily,
    };
  }

  getLabelStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.Label?.color ||
        this.brandTheme?.contentColor ||
        this.themeConfig?.primaryColor ||
        "var(--Neutral-Black-300, #939393)",
      ...this.styleConfig?.Label,
      fontFamily: this.themeConfig?.fontFamily,
      // fontSize: "16px",
    };
  }

  getRatingStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.Rating,
    };
  }

  getInputStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.Input?.borderColor || this.themeConfig?.borderColor,
      color:
        this.styleConfig?.Input?.color ||
        this.brandTheme?.primaryColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Input,
    };
  }

  getInputIconStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.IconColor?.color ||
        this.brandTheme?.IconColor?.color ||
        this.themeConfig?.IconColor?.color,
    };
  }

  getTextAreaStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.TextArea?.borderColor ||
        this.themeConfig?.borderColor,
      color:
        this.styleConfig?.TextArea?.color ||
        this.brandTheme?.primaryColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.TextArea,
    };
  }

  getSingleChoiceStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.SingleChoice?.style?.borderColor ||
        this.themeConfig?.borderColor,
      color:
        this.styleConfig?.SingleChoice?.style?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.SingleChoice?.style,
    };
  }

  getSingleChoiceSelectedStyles(): React.CSSProperties {
    return {
      accentColor:
        this.styleConfig?.SingleChoice?.selectedStyle?.accentColor ||
        this.questThemeData?.accentColor ||
        this.brandTheme?.primaryColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.SingleChoice?.selectedStyle,
    };
  }
  getSingleChoiceHoverStyles(): React.CSSProperties {
    return {
      background: this.styleConfig?.SingleChoice?.hoverBackground,
    };
  }

  getSingleAccentColor(): string {
    return (
      this.styleConfig?.SingleChoice?.selectedStyle?.accentColor || "#6525B3"
    );
  }

  getMultiChoiceStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.MultiChoice?.style?.borderColor ||
        this.themeConfig?.borderColor,
      color:
        this.styleConfig?.MultiChoice?.style?.color ||
        this.themeConfig?.secondaryColor ||
        this.brandTheme?.titleColor,
      ...this.styleConfig?.MultiChoice?.style,
    };
  }

  getMultiChoiceSelectedStyles(): React.CSSProperties {
    return {
      borderColor:
        this.styleConfig?.MultiChoice?.selectedStyle?.borderColor ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor,
      color:
        this.styleConfig?.MultiChoice?.selectedStyle?.color ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor ||
        this.brandTheme?.primaryColor ||
        this.themeConfig?.primaryColor,
      accentColor:
        this.styleConfig?.MultiChoice?.selectedStyle?.accentColor ||
        this.brandTheme?.primaryColor ||
        this.questThemeData?.accentColor,
      ...this.styleConfig?.SingleChoice?.selectedStyle,
    };
  }

  getMultiChoiceSelectedColorStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.MultiChoice?.selectedStyle?.color ||
        this.questThemeData?.accentColor ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
        background: this.brandTheme?.primaryColor || this.questThemeData?.accentColor,
      ...this.styleConfig?.MultiChoice?.selectedStyle,
    };
  }

  getMultiChoiceSelectedSVG(): React.CSSProperties {
    return {
      color:
        this.questThemeData?.accentColor ||
        this.themeConfig?.primaryColor ||
        this.brandTheme?.primaryColor,
    };
  }

  getMultiChoiceSVG(): React.CSSProperties {
    return {
      color:
        this.themeConfig?.borderColor ||
        this.brandTheme?.borderColor ||
        this.styleConfig?.borderColor ||
        this.themeConfig?.borderColor,
    };
  }

  getMultiChoiceBorderStyle(): React.CSSProperties {
    return {
      border:
        this.themeConfig?.borderColor ||
        this.brandTheme?.borderColor ||
        this.styleConfig?.borderColor ||
        this.themeConfig?.borderColor,
    };
  }

  getMultiCholiceOptionSelectedStyle() {
    return {
      color:
        this.styleConfig?.OptionsSelectedColor?.color ||
        this.questThemeData?.accentColor,
      fontFamily: this.themeConfig?.fontFamily,
    };
  }

  getHeaderStyles() {
    return {
      heading: {
        color:
          this.styleConfig?.Heading?.color ||
          "white",
        ...this.styleConfig?.Heading,
        fontFamily: this.themeConfig?.fontFamily,
      },
      description: {
        color:
          this.styleConfig?.Description?.color ||
          this.brandTheme?.contentColor ||
          this.themeConfig?.secondaryColor,
        ...this.styleConfig?.Description,
      },
    };
  }

  getSubHeadingStyles() {
    return {
      color:
        this.styleConfig?.SubHeading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.secondaryColor,
      ...this.styleConfig?.SubHeading,
      paddingBottom: "4px",
      fontFamily: this.themeConfig?.fontFamily,
    };
  }

  getQuestionStyles() {
    return {
      color:
        this.styleConfig?.Question?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Question,
      fontFamily: this.themeConfig?.fontFamily,
    };
  }
  getEmailInputErrorText(): string {
    return this.styleConfig?.EmailError?.text || "Invalid text format";
  }

  getEMailInputErrorStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.EmailError?.errorStyle,
    };
  }
  getTextAreaInputStyles(): React.CSSProperties {
    return {
      borderColor: this.styleConfig?.TextArea?.borderColor,
      color:
        this.styleConfig?.TextArea?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.TextArea,
    };
  }

  getTextAreaInputErrorText(): string {
    return this.styleConfig?.TextAreaError?.text || "Invalid text format";
  }

  getTextAreaInputErrorStyles(): React.CSSProperties {
    return {
      ...this.styleConfig?.TextAreaError?.errorStyle,
    };
  }

  getButtonStyles() {
    // console.log("theme", this.questThemeData);
    return {
      primary: {
        background:
          this.questThemeData?.buttonColor ||
          this.brandTheme?.primaryColor ||
          this.themeConfig?.buttonColor ||
          this.questThemeData?.accentColor ||
          "linear-gradient(84deg, #9035ff 0.36%, #0065ff 100.36%)",
        // ...this.styleConfig?.PrimaryButton,
        color:
          this.styleConfig?.PrimaryButton?.color ||
          "white",
        borderColor:
          this.styleConfig?.PrimaryButton?.borderColor ||
          this.themeConfig?.borderColor ||
          this.brandTheme?.borderColor,
        fontFamily: this.themeConfig?.fontFamily || "Figtree",
        fontWeight:
          this.styleConfig?.PrimaryButton?.fontWeight ||
          this.brandTheme?.fontWeight ||
          this.themeConfig?.fontFamily ||
          "600",
        fontStyle:
          this.styleConfig?.PrimaryButton?.fontStyle ||
          this.brandTheme?.fontWeight ||
          this.themeConfig?.fontFamily ||
          "normal",
        lineHeight:
          this.styleConfig?.PrimaryButton?.lineHeight ||
          this.brandTheme?.fontWeight ||
          this.themeConfig?.fontFamily ||
          "20px",
        border: `none`,
        borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px",
      },
      secondary: {
        color:
          this.styleConfig?.SecondaryButton?.color ||
          "var(--Neutral-White-100, black)",
        ...this.styleConfig?.SecondaryButton,
        background:
          this.styleConfig?.SecondaryButton?.background ||
          "var(--Neutral-White-100, #FFF)",
        borderColor:
          this.styleConfig?.SecondaryButton?.borderColor,
        fontFamily: this.themeConfig?.fontFamily,
        borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px",
      },
    };
  }

  getEmailErrorStyles() {
    return {
      text: this.styleConfig?.EmailError?.text || "This is not a valid email",
      style: this.styleConfig?.EmailError?.errorStyle || {},
    };
  }

  getEmailTextStyles() {
    return {
      text: this.styleConfig?.EmailError?.text || "Invalid email format",
      style: this.styleConfig?.EmailError?.errorStyle || {},
    };
  }

  getFooterStyles() {
    return {
      style: {
        background:
          this.styleConfig?.Footer?.FooterStyle?.backgroundColor ||
          this.styleConfig?.Form?.backgroundColor ||
          this.styleConfig?.Form?.background ||
          this.brandTheme?.background ||
          this.themeConfig?.backgroundColor,
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        ...this.styleConfig?.Footer?.FooterStyle,
      },
      textStyle: this.styleConfig?.Footer?.FooterTextStyle || {},
      iconStyle: this.styleConfig?.Footer?.FooterIcon || {},
      text: this.styleConfig?.Footer?.FooterText,
      link: this.styleConfig?.Footer?.FooterLink,
    };
  }
  getGamifiedQuizFooterStyles(): React.CSSProperties {
    return {
      color:
        this.styleConfig?.Footer?.color || this.themeConfig?.secondaryColor,
      ...this.styleConfig?.Footer,
      fontFamily: this.themeConfig?.fontFamily,
    };
  }

  getTopBarStyles(sections: any, page: number) {
    return {
      headingStyle: this.getHeaderStyles().heading,
      descriptionStyle: this.getHeaderStyles().description,
      iconStyle: { display: "none" },
      topbarStyle: {
        display:
          sections && sections[page]?.showTopBar === false ? "none" : "block",
        ...this.styleConfig?.TopBar,
      },
    };
  }

  getThanksPopUbIconBackgroundStyle() {
    return extractGradientColors(this.questThemeData?.accentColor || this.brandTheme?.primaryColor);
  }

  getContBgStyle() {
    return !(this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? 'linear-gradient(13deg, #fff 38.24%, #9035ff 59.39%, #0065ff 70.7%)' : (this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.includes("gradient") ? (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) : `linear-gradient(14deg, rgba(255, 255, 255, 0) 36.47%, ${hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.6)} 60.34%, ${this.brandTheme?.primaryColor || this.questThemeData?.accentColor} 73.12%)`;
  }

  getTaskLogoColors() {
    return {
      primary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.4) : "#ac69ff",
      secondary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.8) : "#FFFFFF",
      tertiary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 1) : "#6525B3",
    }
  }
}
